import { useState, useEffect, useCallback } from 'react';

import axios, { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

import { Event } from '../components/Agenda/hooks/useNextEvents';
import { FormCompletionStatus } from '../types/onboarding';
import { formCompletionCache } from '../utils/formCompletionCache';
import { OnboardingSessionStorage } from '../utils/sessionStorage';
import { useMenu } from 'global/MenuContext';
import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import { apiMain } from 'services/apiService';
import { findMenuByFormGroupId } from 'utils/formMapper';

// API timeout configuration (5 seconds as per PRD)
const API_TIMEOUT_MS = 5000;

// Error messages in Portuguese for user-friendly feedback
const ERROR_MESSAGES = {
  API_TIMEOUT: 'A solicitação demorou muito tempo. Por favor, tente novamente.',
  NETWORK_ERROR: 'Erro de conexão. Verifique sua internet e tente novamente.',
  INVALID_RESPONSE: 'Resposta inválida do servidor. Tente novamente mais tarde.',
  SESSION_STORAGE: 'Erro ao salvar suas preferências. Continuando sem salvar...',
  GENERIC_ERROR: 'Não foi possível carregar as informações. Tente novamente.',
  NAVIGATION_ERROR: 'Erro ao redirecionar para o formulário. Tente novamente.',
  FORM_COMPLETION_ERROR: 'Erro ao verificar o status do formulário.',
} as const;

/**
 * Helper function to create timeout promise for API calls
 */
const createTimeoutPromise = (timeoutMs: number): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error('API_TIMEOUT'));
    }, timeoutMs);
  });
};

/**
 * Helper function to determine error type and return user-friendly message
 */
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;

    // Network error (no response received)
    if (!axiosError.response) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }

    // API returned an error response
    if (axiosError.response.status >= 500) {
      return ERROR_MESSAGES.INVALID_RESPONSE;
    }

    // Client error (4xx)
    return ERROR_MESSAGES.GENERIC_ERROR;
  }

  // Timeout error
  if (error instanceof Error && error.message === 'API_TIMEOUT') {
    return ERROR_MESSAGES.API_TIMEOUT;
  }

  // Generic error
  return ERROR_MESSAGES.GENERIC_ERROR;
};

export interface UseOnboardingFlowReturn {
  shouldShowOnboarding: boolean;
  shouldShowEventModal: boolean;
  completionStatus: FormCompletionStatus | null;
  handleDoItNow: () => void;
  handleRemindLater: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook for managing event registration onboarding flow
 * Handles form completion detection, timing logic, and user actions
 *
 * @param nextEvent - The next upcoming event from useNextEvents hook (or null if no events)
 */
export const useOnboardingFlow = (nextEvent: Event | null): UseOnboardingFlowReturn => {
  const { user } = useUser();
  const { menu } = useMenu();
  const { toastError } = useToast();
  const navigate = useNavigate();

  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  const [shouldShowEventModal, setShouldShowEventModal] = useState(false);
  const [completionStatus, setCompletionStatus] = useState<FormCompletionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if event is within the time window for prioritization
   *
   * Determines if the event should be prioritized over form completion.
   * This happens in two scenarios:
   * 1. Event is starting soon (within configured time window, default 30 minutes)
   * 2. Event is currently happening (between start and end time)
   *
   * When an event is within this window:
   * - Form completion checks are bypassed
   * - Event Modal is shown immediately
   * - User gets priority access to the event
   *
   * @param event - Event object with initDatetime and endDatetime
   * @returns True if event should be prioritized, false otherwise
   *
   * @example
   * // Config: EVENT_TIME_WINDOW_MINUTES = 30
   * // Event from 14:00 to 15:00, Current time 13:35
   * isWithinEventTimeWindow(event) // true (25 min until event starts)
   *
   * // Event from 14:00 to 15:00, Current time 14:30
   * isWithinEventTimeWindow(event) // true (event is happening now)
   *
   * // Event from 14:00 to 15:00, Current time 15:30
   * isWithinEventTimeWindow(event) // false (event ended)
   */
  const isWithinEventTimeWindow = useCallback((event: Event): boolean => {
    try {
      return event.isUpcoming || event.isOngoing;
    } catch (error) {
      console.error('[useOnboardingFlow] Error checking event time window:', error);
      return false;
    }
  }, []);

  /**
   * Check form completion status with caching
   * Implements 1-minute cache to reduce API calls by ~80%
   * With timeout and comprehensive error handling
   */
  const checkFormCompletion = useCallback(
    async (userId: string, eventId?: string): Promise<FormCompletionStatus | null> => {
      try {
        const cacheEventId = eventId || '';

        // Check cache first - cache hit should return in <5ms
        const cachedStatus = formCompletionCache.get(userId, cacheEventId);
        if (cachedStatus) {
          console.log('[useOnboardingFlow] Using cached form completion status (cache hit)');
          return cachedStatus;
        }

        // Cache miss - fetch from API
        console.log('[useOnboardingFlow] Cache miss - fetching form completion from API');

        // Race between API call and timeout
        const response = await Promise.race([
          apiMain.get(`/forms/v1/analysis/completion/${userId}`),
          createTimeoutPromise(API_TIMEOUT_MS),
        ]);

        // Validate response structure
        if (!response || !response.data) {
          console.error('[useOnboardingFlow] Invalid response structure from completion API');
          throw new Error('INVALID_RESPONSE');
        }

        const data = response.data;

        // Validate data structure
        if (typeof data.completion !== 'number' || !Array.isArray(data.data)) {
          console.error('[useOnboardingFlow] Invalid completion data structure:', data);
          throw new Error('INVALID_RESPONSE');
        }

        // Find first incomplete form group
        const firstIncompleteGroup = data.data?.find((group: any) => group.completion < 100);

        const completionStatus: FormCompletionStatus = {
          userId,
          eventId: cacheEventId,
          completionPercentage: data.completion || 0,
          incompleteFields: firstIncompleteGroup?.fields || [],
          lastChecked: new Date(),
          nextIncompleteSection: firstIncompleteGroup?.group_id || '',
        };

        console.log('[useOnboardingFlow] Form completion status:', completionStatus);

        // Cache the result with 1-minute TTL (60000ms)
        formCompletionCache.set(userId, cacheEventId, completionStatus);

        return completionStatus;
      } catch (error) {
        console.error('[useOnboardingFlow] Error checking form completion:', error);

        const errorMessage = getErrorMessage(error);
        console.error('[useOnboardingFlow] Form completion error details:', errorMessage);

        // Don't show toast here - handled in main flow
        return null;
      }
    },
    []
  );

  /**
   * Get path for incomplete form section
   * With error handling for invalid menu structure
   */
  const getFormGroupPath = useCallback(
    (groupId: string): string => {
      try {
        if (!groupId) {
          console.warn('[useOnboardingFlow] No group ID provided');
          return '/user-profile'; // Fallback
        }

        const menuItem = findMenuByFormGroupId(menu, groupId);

        if (!menuItem || !menuItem.path) {
          console.warn('[useOnboardingFlow] No menu item found for group:', groupId);
          return '/user-profile'; // Fallback
        }

        return menuItem.path;
      } catch (error) {
        console.error('[useOnboardingFlow] Error getting form group path:', error);
        return '/user-profile'; // Fallback
      }
    },
    [menu]
  );

  /**
   * Decide whether the Event Modal should be shown
   * - True if there is an event and it is within the priority time window
   * - True if there is an event and forms are complete (>= 100%)
   */
  const shouldDisplayEventModal = useCallback(
    (event: Event | null, completion: FormCompletionStatus | null): boolean => {
      if (!event) return false;

      if (isWithinEventTimeWindow(event)) return true;

      if (completion && completion.completionPercentage >= 100) return true;

      return false;
    },
    [isWithinEventTimeWindow]
  );

  /**
   * Decide whether the Onboarding Modal should be shown
   * - Applies remind-later preference only to onboarding
   * - True when completion exists and is incomplete (< 100%)
   * - Works with or without an event
   */
  const shouldDisplayOnboardingModal = useCallback(
    (completion: FormCompletionStatus | null, hasRemindLater: boolean): boolean => {
      if (hasRemindLater) return false;

      if (!completion) return false;

      return completion.completionPercentage < 100;
    },
    []
  );

  /**
   * Handle "Do it now" action
   * Routes user to next incomplete form section
   * With comprehensive error handling and fallback to Event Modal
   */
  const handleDoItNow = useCallback(() => {
    try {
      if (!completionStatus) {
        console.error('[useOnboardingFlow] No completion status available');
        toastError(ERROR_MESSAGES.NAVIGATION_ERROR);
        // Fallback to Event Modal
        setShouldShowOnboarding(false);
        setShouldShowEventModal(true);
        return;
      }

      // If form is already complete, go to Event Modal
      if (completionStatus.completionPercentage >= 100) {
        console.log('[useOnboardingFlow] Form already complete, showing Event Modal');
        setShouldShowOnboarding(false);
        setShouldShowEventModal(true);
        return;
      }

      // Navigate to first incomplete section
      const formPath = getFormGroupPath(completionStatus.nextIncompleteSection);
      console.log('[useOnboardingFlow] Navigating to:', formPath);

      // Validate path before navigation
      if (!formPath || formPath === '') {
        console.error('[useOnboardingFlow] Invalid form path');
        toastError(ERROR_MESSAGES.NAVIGATION_ERROR);
        // Fallback to Event Modal
        setShouldShowOnboarding(false);
        setShouldShowEventModal(true);
        return;
      }

      navigate(formPath);

      // Close onboarding modal after successful navigation
      setShouldShowOnboarding(false);
    } catch (error) {
      console.error('[useOnboardingFlow] Error in handleDoItNow:', error);
      toastError(ERROR_MESSAGES.NAVIGATION_ERROR);
      // Fallback to Event Modal on error
      setShouldShowOnboarding(false);
      setShouldShowEventModal(true);
    }
  }, [completionStatus, getFormGroupPath, navigate, toastError]);

  /**
   * Handle "Remind me later" action
   * Saves preference and closes modal
   * With session storage error handling
   */
  const handleRemindLater = useCallback(() => {
    try {
      if (!user?.id) {
        console.error('[useOnboardingFlow] Missing user or event data for remind later');
        // Still allow closing the modal even if we can't save preference
        setShouldShowOnboarding(false);
        return;
      }

      // Try to save preference to session storage
      try {
        const saved = OnboardingSessionStorage.saveRemindLaterPreference(user.id);

        if (saved) {
          console.log('[useOnboardingFlow] Remind later preference saved');
        } else {
          console.warn('[useOnboardingFlow] Failed to save remind later preference');
          // Show warning but don't block user action
          toastError(ERROR_MESSAGES.SESSION_STORAGE);
        }
      } catch (storageError) {
        console.error('[useOnboardingFlow] Session storage error:', storageError);
        toastError(ERROR_MESSAGES.SESSION_STORAGE);
        // Continue anyway - user can still close modal
      }

      // Close onboarding modal, allow normal home page access
      setShouldShowOnboarding(false);
      setShouldShowEventModal(false);
    } catch (error) {
      console.error('[useOnboardingFlow] Error in handleRemindLater:', error);
      // Gracefully close modal even on error
      setShouldShowOnboarding(false);
      setShouldShowEventModal(false);
    }
  }, [user, nextEvent, toastError]);

  /**
   * Main logic to determine which modal to show
   * With comprehensive error handling and graceful fallbacks
   */
  const evaluateOnboardingFlow = useCallback(async () => {
    if (!user) {
      console.log('[useOnboardingFlow] No user found, skipping onboarding flow');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1) Remind later preference (applies only to onboarding, independent of event)
      let hasRemindLater = false;
      try {
        hasRemindLater = OnboardingSessionStorage.hasRemindLaterPreference(user.id);
      } catch (storageError) {
        console.error('[useOnboardingFlow] Error checking remind later preference:', storageError);
        hasRemindLater = false;
      }

      // 2) Fetch completion once (works with or without event)
      const completion = await checkFormCompletion(user.id, nextEvent?.id ?? undefined);
      if (completion) {
        setCompletionStatus(completion);
      }

      // 3) Compute decisions independently
      const eventModal = shouldDisplayEventModal(nextEvent, completion);
      const onboardingModal = shouldDisplayOnboardingModal(completion, hasRemindLater);

      // 4) Error fallback when event exists and completion lookup failed
      if (nextEvent && !completion && !isWithinEventTimeWindow(nextEvent)) {
        console.error('[useOnboardingFlow] Failed to check completion, defaulting to Event Modal');
        const errorMsg = ERROR_MESSAGES.FORM_COMPLETION_ERROR;
        setError(errorMsg);
        toastError(errorMsg);

        setShouldShowEventModal(true);
        setShouldShowOnboarding(false);
        return;
      }

      // 5) Apply final gating: onboarding is hidden if event modal should show
      setShouldShowEventModal(eventModal);
      setShouldShowOnboarding(onboardingModal && !eventModal);
    } catch (error) {
      console.error('[useOnboardingFlow] Error evaluating onboarding flow:', error);

      // Get user-friendly error message
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      toastError(errorMessage);

      // Graceful fallback: Default to Event Modal on error (as per PRD)
      setShouldShowOnboarding(false);
      setShouldShowEventModal(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    user,
    nextEvent,
    isWithinEventTimeWindow,
    checkFormCompletion,
    toastError,
    shouldDisplayEventModal,
    shouldDisplayOnboardingModal,
  ]);

  /**
   * Run evaluation on mount and when user changes
   */
  useEffect(() => {
    evaluateOnboardingFlow();
  }, [evaluateOnboardingFlow]);

  return {
    shouldShowOnboarding,
    shouldShowEventModal,
    completionStatus,
    handleDoItNow,
    handleRemindLater,
    isLoading,
    error,
  };
};
